import { InsightFilter } from '@toggle/toggle';
import React from 'react';

import { useEntity } from '~/hooks/use-entities';
import { useInsightDrawer } from '~/hooks/use-insight-drawer/useInsightDrawer';
import { appPaths } from '~/routes/app-paths';
import { isOnPage } from '~/services/tracking/posthog/utils';
import { getExploreDirection } from '~/views/dashboard/whats-new-widget/components/insight-card/utils';
import { TruncateCombinedIcons } from '~/views/insights/components/truncate-combined-icons/TruncateCombinedIcons';
import { TruncateSingleIcons } from '~/views/insights/components/truncate-single-icons/TruncateSingleIcons';

export type InsightsIconProps = {
  insight: InsightFilter;
  isDisabled: boolean;
  variant?: 'chart' | 'assets-table';
};

export const InsightsIcon = ({
  insight,
  isDisabled,
  variant = 'chart',
}: InsightsIconProps) => {
  const { openDrawer } = useInsightDrawer();
  const { data: entityData } = useEntity(insight.metadata.entity);
  const isOnWatchlist = isOnPage(appPaths.watchlist);

  const isCombined = insight.metadata.kind === 'combination';

  const handleSelect = (insightId: string) => {
    openDrawer(insightId, {
      source: isOnWatchlist ? 'watchlist' : 'dashboard_watchlist',
    });
  };

  const direction = getExploreDirection(insight.metadata.direction);

  if (isCombined) {
    const displayedInsights = insight.metadata.conditions.map(c => ({
      ...c,
      name: insight.card.name,
      insightId: insight.id,
      median: insight.metadata.median_return,
      createdAt: insight.created_at,
      direction,
      horizon: insight.metadata.horizon,
      episodesCount: insight.metadata.episode_count,
      stars: insight.metadata.stars,
      type: insight.metadata.type,
      matched: !isDisabled,
    }));

    return (
      <TruncateCombinedIcons
        displayedInsights={[displayedInsights]}
        shouldTruncate={false}
        entity={entityData?.data}
        handleSelect={!isDisabled ? handleSelect : undefined}
        variant={variant}
      />
    );
  }

  const formattedInsight = {
    card: { name: insight.card.name },
    id: insight.id,
    median: insight.metadata.median_return,
    created_at: insight.created_at,
    direction,
    matched: !isDisabled,
    best_horizon: insight.metadata.horizon,
    conditions: insight.metadata.conditions,
    episode_count: insight.metadata.episode_count,
    stars: insight.metadata.stars,
    kind: insight.metadata.kind,
  };

  return (
    <TruncateSingleIcons
      insights={[formattedInsight]}
      entity={entityData?.data}
      handleSelect={!isDisabled ? handleSelect : undefined}
      variant={variant}
    />
  );
};
